import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { AccountType, RoutePaths } from 'Constants/Common';
import { PaymentApi } from 'api/PaymentApi';
import {
    BankValidationResponse,
    CustomerPolicies
} from '../../common/CommonTypes';
import FormInput from '../../common/FormInput';
import ErrorSummary from '../../common/ErrorSummary/ErrorSummary';
import PageTitle from '../../common/PageTitle';
import BankDetailsForm, { BankDetailsErrors } from './BankDetailsForm';
import Checkbox from '../../common/checkbox/checkbox';
import Button from '../../common/button/Button';
import { useBankDetails } from './useBankDetails';
import { useAppSelector } from 'state/hooks';
import useScrollToView from 'components/hooks/useScrollToView';

const AddBankDetailsPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { getAccessTokenSilently } = useAuth0();
    const scrollRef = useScrollToView();

    const businessPartnerId = useAppSelector(
        (s) => s.customer.defaultBusinessPartnerId
    );
    const policyReference = useAppSelector(
        (s) => s.customer.defaultPolicyReference
    );
    const customerEmail = useAppSelector((s) => s.customer.email) ?? '';
    const vehicles = useAppSelector((s) => s.vehicles.vehicleList);

    const {
        bankDetails,
        daysOptions,
        onChange,
        onPaymentDayChange,
        onSortCodeChange,
        validateBankDetails
    } = useBankDetails();

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isPolicyAccepted, setIsPolicyAccepted] = useState(false);

    const [bankDetailsErrors, setBankDetailsErrors] =
        useState<BankDetailsErrors>({});

    const onBack = () => {
        navigate(RoutePaths.PersonalDetails);
    };

    const errors = Object.values(bankDetailsErrors).filter(Boolean);

    const onCreateDisbursementSuccess = (data: BankValidationResponse) => {
        setIsSubmitting(false);

        if (!data.isSuccess) {
            setBankDetailsErrors({
                bankValidation:
                    t('add_bank_details.failure_message') +
                    ` ${data.text ? data.text : ''}`
            });
            return;
        }

        setIsSubmitted(true);
        toast.success(t('add_bank_details.success_message'), {
            // to force bank details api call
            onClose: () => navigate(RoutePaths.Home)
        });
    };

    const onCreateDisbursementFailure = (error: string) => {
        setIsSubmitting(false);
        setBankDetailsErrors({
            bankValidation:
                t('add_bank_details.failure_message') + ` ${error ? error : ''}`
        });
    };

    const validateForm = (): boolean => {
        const errors: BankDetailsErrors = {};

        if (!isPolicyAccepted) {
            errors.directDebitPolicy = t('add_bank_details.confirmation_error');
        }

        const { isValid: isBankDetailsValid, validationErrors } =
            validateBankDetails();

        if (!isBankDetailsValid) {
            Object.assign(errors, validationErrors);
        }

        setBankDetailsErrors(errors);

        return isBankDetailsValid && isPolicyAccepted;
    };

    const onSubmit = async () => {
        const isFormValid = validateForm();

        if (!isFormValid) {
            return;
        }

        setIsSubmitting(true);

        const token = await getAccessTokenSilently();
        const controller = new AbortController();

        const vehicle = vehicles.find((v) =>
            v.policies.some((policy) => policy.reference === policyReference)
        );

        const policyDetails: CustomerPolicies = {
            productRefNumbers:
                vehicle?.policies
                    .map((policy) => policy.reference)
                    .filter((reference) => reference === policyReference) ?? [],
            vehicleRegNumber: vehicle?.vrn ?? '',
            customerEmail
        };

        new PaymentApi().CreateDisbursement(
            {
                accountHolderName: bankDetails.accountHolderName,
                sortCode1: bankDetails.sortCode[0].value,
                sortCode2: bankDetails.sortCode[1].value,
                sortCode3: bankDetails.sortCode[2].value,
                accountNumber: bankDetails.accountNumber,
                selectedPaymentDay: '',
                accountType: AccountType.Disbursement,
                businessPartnerId,
                referenceNumber: policyReference,
                policies: [policyDetails]
            },
            token,
            onCreateDisbursementSuccess,
            onCreateDisbursementFailure,
            controller.signal
        );
    };

    const onConfirmationChange = () => {
        setIsPolicyAccepted(!isPolicyAccepted);
    };

    return (
        <div className='card page-content' ref={scrollRef}>
            <PageTitle title={t('add_bank_details.title')} />

            <p className='p-3 mb-0' style={{ whiteSpace: 'pre-wrap' }}>
                {t('add_bank_details.instruction')}
            </p>

            <BankDetailsForm
                bankDetails={bankDetails}
                bankDetailErrors={bankDetailsErrors}
                daysOptions={daysOptions}
                t={t}
                onChange={onChange}
                onSortCodeChange={onSortCodeChange}
                onPaymentDayChange={onPaymentDayChange}
                showPaymentDay={false}
            />

            <FormInput
                id='disbursement-confirmation'
                errorText={bankDetailsErrors.directDebitPolicy}>
                <Checkbox
                    id='disbursement-confirmation-checkbox'
                    checked={isPolicyAccepted}
                    text={t('add_bank_details.confirmation_label')}
                    onChange={onConfirmationChange}
                />
            </FormInput>

            <div className='card-footer page-content-footer'>
                <ErrorSummary
                    title={t('page_errors')}
                    errors={errors}
                    className='card-error'
                />
            </div>

            <div className='bottom-navigation'>
                <Button
                    text={t('submit')}
                    className='btn btn-primary progress-button'
                    onClick={onSubmit}
                    disabled={isSubmitting || isSubmitted}
                />
                <Button
                    text={t('back')}
                    className='btn btn-secondary progress-button'
                    onClick={onBack}
                    disabled={isSubmitted}
                />
            </div>
        </div>
    );
};

export default AddBankDetailsPage;
